import { namesTheTree, resolve, type Schema } from './schema'

// Where a type keeps its placements, read off its fields schema.
//
// The intent schemas say a type can be placed into and how; this says
// *where the result lands*, which is the other half a control needs and
// the last piece that was still hardcoded. `PageEditor` knew that a
// Page's placements live under `slots`; nothing knew that a Container's
// live under `items`, a Grid's under `cells[].link`, or that both mean
// the same thing to a list drawn on screen.
//
// It is derived rather than listed for the reason everything else here
// is: a tenth component type's arrangement is readable the day it is
// registered, and a ninth one renaming its field breaks nothing.
//
// The marker doing the work is `x-link`, which the fields schema already
// carried — this only had to notice *how* it was wrapped.

/**
 * One place a type keeps placements.
 *
 * Four shapes, and they are the four the registered types actually use.
 * The distinction that matters to a control is `keyed` against the rest:
 * a keyed arrangement has named regions and is served by the type's
 * slot-carrying intents, and everything else is one list served by the
 * intents without a slot.
 */
export type ArrangementKind =
  /** A region name to its own ordered list — a Page's slots, a site's defaults. */
  | { kind: 'keyed'; linkAt?: string }
  /** One ordered list — a Container's items, a Grid's cells. */
  | { kind: 'list'; linkAt?: string }
  /** Exactly one placement, replaced rather than inserted — a site's 404. */
  | { kind: 'single' }
  /**
   * A shape of this type's own that holds placements somewhere inside it,
   * and is not a list of them — a website's routing tree, which is
   * recursive and carries a slug and a nav flag per node.
   *
   * Named rather than ignored, so a caller that cannot draw one can say
   * so instead of showing a component with no placements at all.
   */
  | { kind: 'tree' }

export type Arrangement = ArrangementKind & { property: string }

/**
 * Every arrangement a type declares, in declaration order.
 *
 * A property qualifies by *reaching* an `x-link`, so a Form's `fields`
 * and a Media's `derivatives` — both arrays of objects, neither holding a
 * link — are correctly not arrangements. That is the same test
 * `namesTheTree` applies to a payload, asked of a document instead.
 */
export function arrangementsOf(fields: Schema | undefined, root: Schema): Arrangement[] {
  const resolved = resolve(fields, root)
  if (!resolved?.properties) return []

  return Object.entries(resolved.properties).flatMap(([property, schema]) => {
    const kind = classify(schema, root)
    return kind ? [{ ...kind, property }] : []
  })
}

function classify(schema: Schema, root: Schema): ArrangementKind | undefined {
  const resolved = resolve(schema, root)
  if (!resolved) return undefined

  // A bare link: one placement, and setting it replaces what was there.
  if (resolved['x-link']) return { kind: 'single' }

  if (resolved.type === 'array') {
    const item = resolve(resolved.items, root)
    if (!item) return undefined
    if (item['x-link']) return { kind: 'list' }
    // A list of records, each naming one placement and carrying its own
    // arrangement values beside it — a Grid's cell holds a span and an
    // alignment next to the link.
    const linkAt = linkProperty(item, root)
    return linkAt ? { kind: 'list', linkAt } : undefined
  }

  if (resolved.type === 'object') {
    // A map: the key is the region's name. It is `additionalProperties`
    // rather than `properties` because the set of regions is open — the
    // type declares a vocabulary and nothing refuses a name outside it.
    if (typeof resolved.additionalProperties === 'object') {
      const value = resolve(resolved.additionalProperties, root)
      if (value?.type !== 'array') return undefined
      const item = resolve(value.items, root)
      if (!item) return undefined
      if (item['x-link']) return { kind: 'keyed' }
      const linkAt = linkProperty(item, root)
      return linkAt ? { kind: 'keyed', linkAt } : undefined
    }
    // An object of this type's own that holds placements somewhere
    // inside. Reported so a caller can say it exists and go no further.
    if (namesTheTree(resolved, root)) return { kind: 'tree' }
  }

  return undefined
}

/** The member of a record that names the placement, if exactly one does. */
function linkProperty(item: Schema, root: Schema): string | undefined {
  if (item.type !== 'object') return undefined
  const linked = Object.entries(item.properties ?? {}).filter(
    ([, property]) => resolve(property, root)?.['x-link'],
  )
  // One, or none. Two would make "which placement is this record?"
  // ambiguous, and guessing would arrange the wrong one.
  return linked.length === 1 ? linked[0][0] : undefined
}

/**
 * The ordered link ids one arrangement names, read out of a document's
 * stored fields.
 *
 * `keyed` is answered per region, so the caller passes the region it
 * wants; every other shape has one list and ignores it.
 */
export function linkIdsIn(
  arrangement: Arrangement,
  fields: Record<string, unknown>,
  slot?: string,
): string[] {
  const stored = fields[arrangement.property]

  if (arrangement.kind === 'single') {
    return typeof stored === 'string' && stored ? [stored] : []
  }
  if (arrangement.kind === 'tree') return []

  const list =
    arrangement.kind === 'keyed'
      ? slot !== undefined && stored && typeof stored === 'object'
        ? (stored as Record<string, unknown>)[slot]
        : undefined
      : stored

  if (!Array.isArray(list)) return []
  return list.flatMap((entry) => {
    if (arrangement.linkAt === undefined) return typeof entry === 'string' ? [entry] : []
    const linkId = (entry as Record<string, unknown> | null)?.[arrangement.linkAt]
    return typeof linkId === 'string' ? [linkId] : []
  })
}

/** The region names a keyed arrangement currently stores. */
export function storedRegions(arrangement: Arrangement, fields: Record<string, unknown>): string[] {
  if (arrangement.kind !== 'keyed') return []
  const stored = fields[arrangement.property]
  return stored && typeof stored === 'object' ? Object.keys(stored) : []
}

/**
 * Every link id a value holds, wherever the schema says one lives.
 *
 * `linkIdsIn` answers "what is in this list, in order" and only the
 * shapes a control draws can answer it. This answers the different
 * question "is this child accounted for anywhere at all", and it has to
 * cover the shapes no control draws — a website's routing tree holds the
 * placement for every page it routes, and those are arranged, however
 * little this client can do about arranging them.
 *
 * Getting that wrong is not cosmetic: children nothing accounts for are
 * reported as composing nowhere, and telling an author that every page on
 * their site composes nowhere is the worst sentence this client could
 * produce.
 *
 * The walk is guided by the schema rather than by the value's shape, so a
 * string that happens to sit where a link goes is a link and a string
 * anywhere else is left alone.
 */
export function linksIn(schema: Schema | undefined, value: unknown, root: Schema): string[] {
  const resolved = resolve(schema, root)
  if (!resolved || value === null || value === undefined) return []

  if (resolved['x-link']) return typeof value === 'string' && value ? [value] : []

  if (Array.isArray(value)) {
    return value.flatMap((entry) => linksIn(resolved.items, entry, root))
  }

  if (typeof value !== 'object') return []
  const record = value as Record<string, unknown>

  // A map's values all share one schema; an object's are named. A shape
  // may legitimately be both — nothing registered is, and answering both
  // costs a concatenation.
  const keyed =
    typeof resolved.additionalProperties === 'object'
      ? Object.values(record).flatMap((entry) => linksIn(resolved.additionalProperties as Schema, entry, root))
      : []
  const named = Object.entries(resolved.properties ?? {}).flatMap(([name, property]) =>
    linksIn(property, record[name], root),
  )
  return [...keyed, ...named]
}
